import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  selectLoggedInUser,
  createUserAsync,
  sendOTPAsync,
  verifyOTPAsync,
  createUserWithOTPAsync,
  selectOtpSent,
  selectOtpVerified,
  selectTempToken,
  selectRegistrationEmail,
  selectError,
  selectStatus,
} from '../authSlice';
import { Link } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import {
  EnvelopeIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

export default function Signup() {
  const dispatch = useDispatch();
  const user = useSelector(selectLoggedInUser);
  const otpSent = useSelector(selectOtpSent);
  const otpVerified = useSelector(selectOtpVerified);
  const tempToken = useSelector(selectTempToken);
  const registrationEmail = useSelector(selectRegistrationEmail);
  const error = useSelector(selectError);
  const status = useSelector((state) => state.auth.status);

  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'password'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
  } = useForm();

  // Timer for OTP expiration
  useEffect(() => {
    if (otpSent && !otpVerified) {
      setOtpTimer(600); // 10 minutes
    }
  }, [otpSent, otpVerified]);

  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    } else if (otpTimer === 0 && otpSent && !otpVerified) {
      setResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [otpTimer, otpSent, otpVerified]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      return;
    }
    setResendDisabled(true);
    dispatch(sendOTPAsync(email));
    setStep('otp');
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp) {
      return;
    }
    dispatch(verifyOTPAsync({ email, otp }));
  };

  useEffect(() => {
    if (otpVerified && tempToken) {
      setStep('password');
    }
  }, [otpVerified, tempToken]);

  const handleCreateAccount = async (data) => {
    if (data.password !== data.confirmPassword) {
      return;
    }

    dispatch(
      createUserWithOTPAsync({
        email: registrationEmail,
        password: data.password,
        tempToken,
      })
    );
  };

  return (
    <>
      {user && <Navigate to="/" replace={true}></Navigate>}
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-white rounded-xl shadow-subtle border border-slate-200 p-8 sm:p-10">
            {/* Logo */}
            <div className="text-center mb-8">
              <img
                className="mx-auto h-12 w-auto"
                src="/ecommerce.png"
                alt="SubifyHub"
              />
              <h2 className="mt-6 text-2xl font-light tracking-tight text-slate-900">
                Create Your Account
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Join SubifyHub for premium digital services
              </p>
            </div>

            {/* Step Indicator */}
            <div className="mb-8 flex items-center justify-between">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step === 'email' || step === 'otp' || step === 'password'
                      ? 'bg-sky-500 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  1
                </div>
                <p className="text-xs text-slate-500 mt-2">Email</p>
              </div>
              <div className="flex-1 h-1 mx-2 mt-5 bg-slate-200" />
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step === 'otp' || step === 'password'
                      ? 'bg-sky-500 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  2
                </div>
                <p className="text-xs text-slate-500 mt-2">OTP</p>
              </div>
              <div className="flex-1 h-1 mx-2 mt-5 bg-slate-200" />
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step === 'password'
                      ? 'bg-sky-500 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  3
                </div>
                <p className="text-xs text-slate-500 mt-2">Password</p>
              </div>
            </div>

            {/* Error Messages */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">
                  {typeof error === 'object' ? error.message || 'An error occurred' : error}
                </p>
              </div>
            )}

            {/* Step 1: Email */}
            {step === 'email' && (
              <form onSubmit={handleSendOTP} className="space-y-5">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-700 mb-2"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    We'll send a verification code to this email
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading' || !email}
                  className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-medium py-3.5 px-6 rounded-xl transition-all duration-200 text-sm tracking-wide uppercase disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? 'Sending...' : 'Send OTP'}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </form>
            )}

            {/* Step 2: OTP Verification */}
            {step === 'otp' && (
              <form onSubmit={handleVerifyOTP} className="space-y-5">
                <div>
                  <label
                    htmlFor="otp"
                    className="block text-sm font-medium text-slate-700 mb-2"
                  >
                    Verification Code
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <ShieldCheckIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="otp"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength="6"
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400 text-center text-2xl tracking-widest font-mono"
                      placeholder="000000"
                      required
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <p className="text-slate-500">
                      OTP sent to {email}
                    </p>
                    <div className="flex items-center gap-2 text-sky-600">
                      <ClockIcon className="w-4 h-4" />
                      {otpTimer > 0 ? (
                        <span>{formatTime(otpTimer)}</span>
                      ) : (
                        <span className="text-red-500">Expired</span>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading' || !otp || otpTimer === 0}
                  className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-medium py-3.5 px-6 rounded-xl transition-all duration-200 text-sm tracking-wide uppercase disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? 'Verifying...' : 'Verify OTP'}
                  <CheckCircleIcon className="w-4 h-4" />
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleSendOTP}
                    disabled={resendDisabled || status === 'loading'}
                    className="text-sm text-sky-600 hover:text-sky-500 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {resendDisabled && otpTimer > 0
                      ? `Resend in ${formatTime(otpTimer)}`
                      : 'Resend OTP'}
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: Password Setup */}
            {step === 'password' && (
              <form
                onSubmit={handleSubmit(handleCreateAccount)}
                className="space-y-5"
                noValidate
              >
                {/* Password Field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-slate-700 mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="password"
                      {...register('password', {
                        required: 'Password is required',
                        pattern: {
                          value:
                            /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/gm,
                          message:
                            'Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number',
                        },
                      })}
                      type={showPassword ? 'text' : 'password'}
                      className="w-full pl-10 pr-12 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                      placeholder="Create a strong password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-slate-700 mb-2"
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <ShieldCheckIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value, formValues) =>
                          value === formValues.password ||
                          'Passwords do not match',
                      })}
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="w-full pl-10 pr-12 py-3 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition text-slate-900 placeholder:text-slate-400"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-medium py-3.5 px-6 rounded-xl transition-all duration-200 text-sm tracking-wide uppercase disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? 'Creating Account...' : 'Create Account'}
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </form>
            )}

            {/* Login Link */}
            <div className="mt-8 text-center">
              <p className="text-sm text-slate-500">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-sky-600 hover:text-sky-500 transition-colors"
                >
                  Log In
                </Link>
              </p>
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-400">
              Your information is secure and will never be shared
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
